import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import HeroSection from '@/components/HeroSection';
import ProgramCard from '@/components/ProgramCard';
import { ADULT_PROGRAMS, INSTRUCTORS } from '@/lib/constants';
import InstagramFeed from '@/components/InstagramFeed';
import SEO from '@/components/SEO';
import TrackedBookingLink from '@/components/TrackedBookingLink';
import { ArrowRight, Trophy, Users, Calendar } from 'lucide-react';

const FadeInWhenVisible = ({ children, delay = 0 }: { children: React.ReactNode, delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

export default function Home() {
  const founder = INSTRUCTORS[0];

  return (
    <div className="bg-brand-black">
      <SEO
        title="Brazilian Jiu-Jitsu Training Culver City | Delta BJJ"
        description="Learn Brazilian Jiu-Jitsu in Culver City at the award-winning Delta BJJ. Led by 6th-degree black belt Gerson Sanginitto. Try a free trial class!"
        image="/images/hero-team.jpg"
        url="https://deltabjj.com"
      />

      {/* Hero Section */}
      <HeroSection
        title="Respect • Honor • Discipline • Excellence"
        primaryButton={{
          text: 'Start Your Jiu-Jitsu Journey',
          href: '/#book-trial',
        }}
        secondaryButton={{
          text: 'Explore Jiu-Jitsu Training Programs',
          href: '/brazilian-jiu-jitsu-training-programs',
        }}
      />

      {/* Legacy Section - Editorial Style */}
      <section className="relative py-32 md:py-48 px-6 lg:px-12 bg-white overflow-hidden">
        {/* Decorative Background Text */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-[0.02] flex items-center justify-center select-none">
          <span className="text-[30vw] font-display font-bold leading-none">DELTA</span>
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-20 items-center">
          <div className="lg:col-span-7">
            <FadeInWhenVisible>
              <span className="text-brand-blue font-display text-xs tracking-[0.4em] uppercase mb-6 block">Our Foundation</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-8xl font-display font-bold text-brand-black mb-12 tracking-tighter leading-tight break-words px-4">
                THE LEGACY OF <span className="text-gray-300 whitespace-nowrap text-3xl sm:text-4xl md:text-5xl lg:text-7xl">EXCELLENCE.</span>
              </h2>
              <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed mb-12 max-w-2xl">
                Founded in 1998, Delta BJJ stands as a pillar of the martial arts community in Culver City. We don't just teach techniques; we cultivate a way of life built on <span className="text-brand-black font-semibold">discipline, respect, and mental fortitude.</span>
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  { icon: Trophy, title: "Expertise", desc: "Led by Brazilian Jiu-Jitsu 6th-degree black belt, Gerson Sanginitto" },
                  { icon: Users, title: "Community", desc: "A supportive environment for all ages and skill levels." },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex gap-4 items-start">
                      <div className="p-3 bg-brand-blue rounded-lg text-white shadow-lg shadow-brand-blue/20">
                        <Icon size={28} strokeWidth={2.5} color="white" className="block" />
                      </div>
                      <div>
                        <h3 className="font-display font-bold text-sm uppercase tracking-wider mb-2">{item.title}</h3>
                        <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </FadeInWhenVisible>
          </div>
          
          <div className="lg:col-span-5 relative">
            <FadeInWhenVisible delay={0.2}>
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm group">
              <img
                src="/images/legacy+bjj.webp"
                  alt="Head Professor Gerson Sanginitto and a student at Delta Brazilian Jiu-Jitsu"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                />
                <div className="absolute inset-0 border-[20px] border-white/20 pointer-events-none" />
              </div>
              {/* Floating Stat */}
              <div className="absolute -bottom-10 -left-10 bg-brand-blue p-10 hidden md:block">
                <span className="block text-5xl font-display font-bold text-white">25+</span>
                <span className="block text-[10px] tracking-[0.3em] font-bold text-white/60 uppercase mt-2">Years of Impact</span>
              </div>
            </FadeInWhenVisible>
          </div>
        </div>
      </section>

      {/* Philosophy Section - Full Width Video/Image with Text Overlay */}
      <section className="relative min-h-[92vh] md:h-[80vh] flex items-center justify-center bg-brand-black py-20 md:py-0">
        <div className="absolute inset-0">
              <img
                src="/images/black-white-action-image.webp"
            alt="Philosophy"
            className="w-full h-full object-cover opacity-40 grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-black via-brand-black/40 to-transparent" />
            </div>
            
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-start lg:items-end">
          <FadeInWhenVisible>
            <div className="max-w-lg">
              <h2 className="text-3xl sm:text-4xl md:text-7xl font-display font-bold text-white mb-6 md:mb-8 tracking-tighter leading-none">
                BECOME <br />
                <span className="blue-gradient-text whitespace-nowrap">UNSTOPPABLE.</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-white/60 mb-8 md:mb-12 max-w-lg leading-relaxed uppercase tracking-wide md:tracking-wider font-light">
                Delta BJJ is more than a gym. It is a haven for those seeking to push their limits and discover their true potential.
              </p>
              <div className="flex flex-col gap-6">
                {["Build Confidence", "Master Technique", "Join the Family"].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-4 text-white/80 group">
                    <div className="w-12 h-[1px] bg-brand-blue group-hover:w-20 transition-all" />
                    <span className="text-xs sm:text-sm font-bold tracking-[0.14em] md:tracking-[0.2em] uppercase">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeInWhenVisible>
          
          <FadeInWhenVisible delay={0.2}>
            <div className="flex flex-col gap-3 md:gap-4 w-full lg:w-auto lg:min-w-[200px] mt-4 md:mt-8 lg:mt-0">
              <Link to="/instructors" className="btn-premium-outline text-center py-3.5 md:py-4 px-6 md:px-8 text-sm md:text-base font-bold tracking-[0.14em] md:tracking-[0.2em] uppercase w-full lg:w-auto">
                Instructors
              </Link>
              <Link to="/schedule" className="btn-premium-outline text-center py-3.5 md:py-4 px-6 md:px-8 text-sm md:text-base font-bold tracking-[0.14em] md:tracking-[0.2em] uppercase w-full lg:w-auto">
                Class Schedule
              </Link>
              <Link to="/brazilian-jiu-jitsu-training-programs" className="btn-premium-outline text-center py-3.5 md:py-4 px-6 md:px-8 text-sm md:text-base font-bold tracking-[0.14em] md:tracking-[0.2em] uppercase w-full lg:w-auto">
                Pricing
              </Link>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Programs Preview */}
      <section className="py-32 md:py-48 px-6 lg:px-12 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-24 gap-12">
            <FadeInWhenVisible>
              <span className="text-brand-blue font-display text-xs tracking-[0.4em] uppercase mb-6 block">Our Curriculum</span>
              <h2 className="text-5xl md:text-8xl font-display font-bold text-brand-black tracking-tighter leading-none">
                CHOOSE YOUR JIU-JITSU <br />
                <span className="text-gray-300">PATH.</span>
              </h2>
            </FadeInWhenVisible>
            <FadeInWhenVisible delay={0.2}>
              <Link to="/brazilian-jiu-jitsu-training-programs" className="group flex items-center gap-4 text-brand-black font-bold tracking-widest uppercase text-base md:text-lg">
                Explore All Programs <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </Link>
            </FadeInWhenVisible>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {ADULT_PROGRAMS.slice(0, 2).map((program, i) => (
              <FadeInWhenVisible key={program.id} delay={i * 0.1}>
                <ProgramCard
                  title={program.title}
                  description={program.description}
                  schedule={program.schedule}
                  image={program.image}
                />
              </FadeInWhenVisible>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section - Premium Profile */}
      <section className="py-32 md:py-48 px-6 lg:px-12 bg-brand-black text-white relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-blue/5 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-20 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <FadeInWhenVisible>
              <div className="relative group">
                <div className="absolute -inset-4 border border-brand-blue/20 translate-x-8 translate-y-8 pointer-events-none" />
                <div className="relative z-10 overflow-hidden rounded-sm">
                  <img
                    src={founder.image || '/images/founder.jpg'}
                    alt={founder.alt || founder.name}
                    className="w-full transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
              </div>
            </FadeInWhenVisible>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <FadeInWhenVisible delay={0.2}>
              <span className="text-brand-silver font-display text-xs tracking-[0.4em] uppercase mb-6 block font-bold">The Mastermind</span>
              <h2 className="text-4xl md:text-7xl font-display font-bold mb-8 leading-none tracking-tighter">
                {founder.title}
              </h2>
              <h2 className="text-4xl md:text-7xl font-display font-bold mb-8 leading-none tracking-tighter">
                <span className="blue-gradient-text">{founder.name}</span>
              </h2>
              <blockquote className="text-2xl md:text-3xl font-light italic text-white/80 mb-12 border-l-4 border-brand-blue pl-8">
                "{founder.quote}"
              </blockquote>
              
              <div className="space-y-4 mb-12">
                {founder.achievements.map((ach, i) => (
                  <div key={i} className="flex items-center gap-4 py-4 border-b border-white/10 group cursor-default">
                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-full bg-white/5 border border-white/10 group-hover:border-brand-blue/50 transition-colors shadow-lg shadow-black/50">
                      <img src="/ibjjf.svg" alt="IBJJF" className="w-8 h-8 brightness-0 invert opacity-90 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <span className="text-base md:text-lg uppercase tracking-widest font-bold text-white/80 group-hover:text-white transition-colors">{ach.title}</span>
                    {ach.year && <span className="text-[10px] font-mono text-white/40 ml-auto">{ach.year}</span>}
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-6">
                <Link to="/instructors" className="btn-premium-outline text-base md:text-lg py-4 px-8 font-extrabold">Delta BJJ Instructors</Link>
              </div>
            </FadeInWhenVisible>
          </div>
        </div>
      </section>

      {/* Booking Section - Glassmorphism */}
      <section id="book-trial" className="relative py-16 md:py-24 px-6 lg:px-12 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <FadeInWhenVisible>
            <span className="text-brand-blue font-display text-xs tracking-[0.4em] uppercase mb-4 block">Ready to Roll?</span>
            <h2 className="text-5xl md:text-8xl font-display font-bold text-brand-black tracking-tighter leading-none mb-6">
              CLAIM YOUR <br />
              <span className="text-gray-300">SPOT NOW.</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 mb-6 max-w-2xl mx-auto leading-relaxed">
              There's no better time to begin your Brazilian Jiu-Jitsu training. Contact us today to learn more about our adult and kids programs and claim your two free trial classes.
            </p>
            <div className="mb-8">
              <Link to="/contact-bjj-culver-city" className="btn-premium-blue inline-block">
                Start Training Today
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-8">
              {[
                { icon: Calendar, label: "2 Free Classes" },
                { icon: Users, label: "All Ages Welcome" },
                { icon: Trophy, label: "Expert Coaching" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <item.icon className="w-5 h-5 text-brand-blue" />
                  <span className="text-xs font-bold uppercase tracking-widest text-gray-500">{item.label}</span>
                </div>
              ))}
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Instagram Section - Horizontal Scroll */}
      <section className="py-32 bg-brand-black text-white relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-blue/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 mb-20 text-center">
          <FadeInWhenVisible>
            <span className="text-brand-silver font-display text-xs tracking-[0.4em] uppercase mb-6 block font-bold">Social Feed</span>
            <h2 className="text-4xl md:text-6xl font-display font-bold text-white tracking-tighter leading-none mb-6">
              JOIN THE DELTA BJJ <br />
              <span className="blue-gradient-text">COMMUNITY</span>
            </h2>
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Discover the art of Brazilian Jiu-Jitsu at Delta BJJ, Culver City's premier academy. We provide a supportive, high-level training environment for all ages and skill levels.
            </p>
            <div className="mb-12">
              <TrackedBookingLink href="/#book-trial" className="btn-premium-outline inline-block">
                CLAIM YOUR FREE TRIAL CLASS
              </TrackedBookingLink>
            </div>
          </FadeInWhenVisible>
        </div>
        <div className="relative z-10">
          <InstagramFeed />
        </div>
      </section>
    </div>
  );
}
